import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Coins, Infinity as InfinityIcon, ArrowDownToLine, Send } from "lucide-react";

/**
 * BalanceCard — top-of-billing-page glass card showing token balance + tier
 * + the two primary CTAs (recharge / transfer).
 */
export default function BalanceCard({
  balance,
  unlimited,
  tier,
  periodEnd,
  onRecharge,
  onTransfer,
}) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="balance-card"
      className="
        nx-glass rounded-3xl p-5
        bg-[rgba(255,255,255,0.025)]
        border border-[rgba(0,229,255,0.18)]
        shadow-[0_0_30px_-12px_rgba(0,229,255,0.4)]
      "
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-[#00E5FF]/85">
            {unlimited ? (
              <InfinityIcon className="h-3.5 w-3.5" strokeWidth={2.25} />
            ) : (
              <Coins className="h-3.5 w-3.5" strokeWidth={1.75} />
            )}
            {t("billing.balance.label")}
          </div>
          <p
            data-testid="balance-amount"
            className="mt-1 text-[40px] sm:text-[44px] font-semibold leading-none text-[#E5E7EB] font-mono tracking-tight"
            style={{ textShadow: "0 0 22px rgba(0,229,255,0.35)" }}
          >
            {unlimited ? "∞" : (balance ?? 0).toLocaleString()}
          </p>
          <p
            className="mt-2 text-[12px] text-[#9CA3AF]"
            data-testid="balance-tier-line"
          >
            {t("billing.balance.current_plan", { tier: tier?.name || "Free" })}
            {periodEnd ? (
              <>
                {" · "}
                <span className="text-[#E5E7EB]/80">
                  {t("billing.balance.renew", { date: periodEnd })}
                </span>
              </>
            ) : null}
          </p>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-2">
        <button
          type="button"
          onClick={onRecharge}
          data-testid="balance-recharge-btn"
          className="
            flex-1 h-10 rounded-full text-[12.5px] font-semibold
            bg-[rgba(0,229,255,0.14)] border border-[rgba(0,229,255,0.5)]
            text-[#00E5FF] hover:bg-[rgba(0,229,255,0.22)]
            shadow-[0_0_18px_rgba(0,229,255,0.25)]
            inline-flex items-center justify-center gap-1.5
          "
        >
          <ArrowDownToLine className="h-3.5 w-3.5" strokeWidth={2} />
          {t("billing.actions.recharge")}
        </button>
        <button
          type="button"
          onClick={onTransfer}
          data-testid="balance-transfer-btn"
          className="
            flex-1 h-10 rounded-full text-[12.5px]
            bg-white/[0.04] border border-white/[0.1]
            text-[#E5E7EB] hover:bg-white/[0.08]
            inline-flex items-center justify-center gap-1.5
          "
        >
          <Send className="h-3.5 w-3.5" strokeWidth={2} />
          {t("billing.actions.transfer")}
        </button>
      </div>
    </div>
  );
}
